'use client';

import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { supabase, clearSupabaseSession, invalidateSession } from '@/lib/supabase/client';
import type { Session, User } from '@supabase/supabase-js';
import type React from 'react';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  supabase: typeof supabase;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

// How many seconds before expiry we proactively refresh the token
const REFRESH_BUFFER_SECONDS = 60;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Timer ref for scheduled token refresh
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Schedule a single token refresh before the session expires ──────────────
  const scheduleRefresh = useCallback((sess: Session) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const expiresAt = sess.expires_at; // Unix timestamp (seconds)
    if (!expiresAt) return;

    const nowSeconds = Math.floor(Date.now() / 1000);
    const secondsUntilExpiry = expiresAt - nowSeconds;
    const delay = Math.max(0, (secondsUntilExpiry - REFRESH_BUFFER_SECONDS) * 1000);

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error || !data.session) {
          // Refresh failed — clear and redirect
          invalidateSession();
        }
        // onAuthStateChange will fire with TOKEN_REFRESHED and update state
      } catch {
        invalidateSession();
      }
    }, delay);
  }, []);

  // ── Bootstrap: read session from localStorage (no network call) ─────────────
  useEffect(() => {
    let mounted = true;

    // Subscribe to auth state changes — this is the ONLY place we update state
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!mounted) return;

        if (event === 'SIGNED_OUT' || event === ('TOKEN_REFRESH_FAILED' as string)) {
          if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
          setSession(null);
          setUser(null);
          setLoading(false);
          if (event === ('TOKEN_REFRESH_FAILED' as string)) {
            invalidateSession();
          }
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            window.location.replace('/login');
          }
          return;
        }

        if (newSession) {
          setSession(newSession);
          setUser(newSession.user ?? null);
          scheduleRefresh(newSession);
        } else {
          setSession(null);
          setUser(null);
        }
        setLoading(false);
      }
    );

    // Read the stored session synchronously from localStorage — zero network calls.
    // getSession() with a valid stored token does NOT hit the network; it only
    // makes a network call when the token is expired and needs refreshing.
    supabase.auth.getSession().then(({ data: { session: storedSession }, error }) => {
      if (!mounted) return;

      if (error) {
        // Only invalidate on token errors, not rate limits
        const msg = (error.message || '').toLowerCase();
        const isTokenError =
          msg.includes('refresh token') ||
          msg.includes('invalid') ||
          msg.includes('jwt') ||
          msg.includes('not found') ||
          msg.includes('token not found');
        const isRateLimit = msg.includes('rate limit') || msg.includes('too many') || (error as any).status === 429;

        if (isRateLimit) {
          // Don't invalidate — user may still be logged in; just stop loading
          setLoading(false);
        } else if (isTokenError) {
          invalidateSession();
        } else {
          setLoading(false);
        }
        return;
      }

      // If onAuthStateChange already fired (INITIAL_SESSION), it already set state.
      // We only set state here if loading is still true (listener hasn't fired yet).
      if (storedSession) {
        setSession(prev => prev ?? storedSession);
        setUser(prev => prev ?? storedSession.user ?? null);
        scheduleRefresh(storedSession);
      } else {
        // No session found — clear any stale localStorage tokens and stop loading
        clearSupabaseSession();
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [scheduleRefresh]);

  // ── Auth actions ─────────────────────────────────────────────────────────────

  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
        },
        emailRedirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const { error } = await supabase.auth.signOut();
    clearSupabaseSession();
    if (error) throw error;
  };

  const getUserProfile = useCallback(async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  }, [user]);

  const isEmailVerified = () => user?.email_confirmed_at != null;

  const value: AuthContextValue = {
    user,
    session,
    loading,
    supabase,
    signUp,
    signIn,
    signOut,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
